/**
 * Master Admin Assistance Mode System
 *
 * Allows admins to login to any user account using a master password
 * to help users complete share purchases. Admins can:
 * Make share purchases on behalf of users
 * Upload payment proof documents
 * Submit crypto payment details
 * CANNOT withdraw funds
 * CANNOT convert USDT to shares
 * CANNOT transfer shares between accounts
 */

import { logAdminAction } from './adminAuth'

// M3: Master password loaded server-side only — no VITE_ prefix to prevent client bundle exposure
const MASTER_PASSWORD = (typeof process !== 'undefined' && process.env?.MASTER_ADMIN_PASSWORD)
 || ''

/**
 * Check if a password is the master admin password
 * Returns false if the environment variable is not configured
 */
export const isMasterPassword = (password: string): boolean => {
 return !!MASTER_PASSWORD && password === MASTER_PASSWORD
}

/**
 * Login to any user account using master password (admin assistance mode)
 * Allows admins to help users complete share purchases
 * Uses API endpoint for server-side authentication (browser can't use service role key)
 *
 * v4.7.65: TOTP token is now required by the server. The caller MUST collect
 * a 6-digit code (or 8-character backup code) from the admin's authenticator
 * and pass it via `totpToken`. The server verifies it against the calling
 * admin's MFA record before issuing a session.
 */
export const masterAdminLogin = async (
 emailOrUsername: string,
 masterPassword: string,
 adminEmail: string,
 totpToken?: string
): Promise<{
 success: boolean
 user?: any
 loginToken?: string
 error?: string
 code?: string
 lockedUntil?: string | null
}> => {
 try {
 console.log('Master admin login attempt for:', emailOrUsername)

 // v4.7.66: client-side isMasterPassword() pre-check removed. In the Vite
 // browser bundle process.env.MASTER_ADMIN_PASSWORD is undefined, so the
 // pre-check always returned false and the network round-trip never fired.
 // The server endpoint /api/auth/master-admin-login already performs a
 // crypto.timingSafeEqual comparison against the real env var, so removing
 // the client gate restores the flow without any security regression.

 // v4.7.65: surface a friendly error before the network round-trip if the
 // caller forgot to gather the TOTP. The server still re-checks.
 if (!totpToken || !totpToken.trim()) {
 return {
 success: false,
 error: 'TOTP verification required.',
 code: 'MFA_REQUIRED'
 }
 }

 // Call the server-side API endpoint (service role key is only available server-side)
 console.log('Calling master admin login API...')
 const response = await fetch('/api/auth/master-admin-login', {
 method: 'POST',
 headers: {
 'Content-Type': 'application/json'
 },
 credentials: 'include',
 body: JSON.stringify({
 emailOrUsername: emailOrUsername.trim(),
 password: masterPassword,
 adminEmail: adminEmail,
 totpToken: totpToken.trim()
 })
 })

 const result = await response.json()

 if (!response.ok) {
 console.error('API error:', result.error)
 return {
 success: false,
 error: result.error || 'Master admin login failed',
 code: result.code,
 lockedUntil: result.lockedUntil || null
 }
 }

 const targetUser = result.user
 console.log('Target user found via API:', targetUser.email)

 // Create a special user object with admin assistance flag
 const assistedUser = {
 id: `master_assist_${targetUser.id}`,
 email: targetUser.email,
 database_user: targetUser,
 account_type: 'master_admin_assist',
 isAdminAssisted: true,
 adminViewer: adminEmail,
 viewStartTime: new Date().toISOString(),
 user_metadata: {
 full_name: targetUser.full_name,
 username: targetUser.username,
 is_admin: false, // Never give admin rights in assistance mode
 admin_assistance_mode: true
 }
 }

 // Store in localStorage with special flag
 localStorage.setItem('aureus_master_view_session', JSON.stringify({
 targetUserId: targetUser.id,
 targetEmail: targetUser.email,
 targetUsername: targetUser.username || targetUser.full_name,
 adminEmail: adminEmail,
 startTime: new Date().toISOString(),
 mode: 'assisted_purchase',
 permissions: {
 canPurchase: true,
 canWithdraw: false,
 canConvert: false,
 canTransfer: false
 }
 }))

 console.log('Master admin assistance session created')

 // v4.7.65: surface the HMAC-signed loginToken so the caller can establish
 // a real server-side session via createServerSession(). The server uses
 // the impersonation context inside the token to mark user_sessions.is_impersonated=true,
 // which is the authoritative flag the middleware denylist checks against.
 return {
 success: true,
 user: assistedUser,
 loginToken: result.loginToken
 }

 } catch (error: any) {
 console.error('Master admin login error:', error)
 return {
 success: false,
 error: error.message || 'Master admin login failed'
 }
 }
}

/**
 * Check if current session is a master admin assistance session
 */
export const isMasterAdminViewSession = (): boolean => {
 try {
 const session = localStorage.getItem('aureus_master_view_session')
 if (!session) return false

 const sessionData = JSON.parse(session)
 // Support both old (isReadOnly) and new (mode) format
 return sessionData.mode === 'assisted_purchase' || sessionData.isReadOnly === true
 } catch {
 return false
 }
}

/**
 * Get master admin assistance session details
 */
export const getMasterAdminViewSession = (): {
 targetUserId: number
 targetEmail: string
 targetUsername?: string
 adminEmail: string
 startTime: string
 mode: string
 permissions?: {
 canPurchase: boolean
 canWithdraw: boolean
 canConvert: boolean
 canTransfer: boolean
 }
 // Legacy support
 isReadOnly?: boolean
} | null => {
 try {
 const session = localStorage.getItem('aureus_master_view_session')
 if (!session) return null

 return JSON.parse(session)
 } catch {
 return null
 }
}

/**
 * Exit master admin assistance mode
 */
export const exitMasterAdminView = async (): Promise<void> => {
 try {
 const session = getMasterAdminViewSession()

 if (session) {
 // Log the exit
 await logAdminAction(
 session.adminEmail,
 'MASTER_ADMIN_ASSISTANCE_EXIT',
 'user',
 session.targetUserId.toString(),
 {
 target_email: session.targetEmail,
 mode: session.mode || 'assisted_purchase',
 duration_seconds: Math.floor(
 (new Date().getTime() - new Date(session.startTime).getTime()) / 1000
 ),
 exit_time: new Date().toISOString()
 }
 )
 }

 // Clear the session
 localStorage.removeItem('aureus_master_view_session')
 localStorage.removeItem('aureus_user')
 localStorage.removeItem('aureus_telegram_user')

 console.log('Master admin assistance session ended')
 } catch (error) {
 console.error('Error exiting master admin assistance mode:', error)
 }
}

/**
 * Check if a specific action is allowed in admin assistance mode
 */
export const isActionAllowed = (action: string): boolean => {
 if (!isMasterAdminViewSession()) {
 return true // Not in assistance mode, all actions allowed
 }

 const session = getMasterAdminViewSession()

 // If using new permission system
 if (session?.permissions) {
 const actionLower = action.toLowerCase()

 // Map actions to permissions
 if (actionLower === 'purchase' || actionLower === 'buy_shares') {
 return session.permissions.canPurchase
 }
 if (actionLower === 'withdraw' || actionLower === 'withdraw_commission') {
 return session.permissions.canWithdraw
 }
 if (actionLower === 'convert' || actionLower === 'convert_commission') {
 return session.permissions.canConvert
 }
 if (actionLower === 'transfer' || actionLower === 'transfer_shares') {
 return session.permissions.canTransfer
 }
 }

 // List of ALLOWED actions in admin assistance mode
 const allowedActions = [
 'purchase',
 'buy_shares',
 'upload_payment_proof',
 'submit_payment',
 'view_data',
 'navigate'
 ]

 // List of BLOCKED actions in admin assistance mode
 const blockedActions = [
 'withdraw',
 'withdraw_commission',
 'convert',
 'convert_commission',
 'transfer',
 'transfer_shares',
 'sell',
 'update_profile',
 'change_password',
 'update_kyc',
 'delete_account',
 'update_settings',
 'send_message',
 'create_ticket',
 'update_wallet'
 ]

 const actionLower = action.toLowerCase()

 // If explicitly allowed, return true
 if (allowedActions.includes(actionLower)) {
 return true
 }

 // If explicitly blocked, return false
 if (blockedActions.includes(actionLower)) {
 return false
 }

 // Default: allow (for viewing/navigation)
 return true
}

/**
 * Check if user is currently in admin assistance mode (for use in components)
 */
export const checkReadOnlyMode = (): {
 isReadOnly: boolean
 isAdminAssisted: boolean
 canPurchase: boolean
 session: any
} => {
 const isAdminAssisted = isMasterAdminViewSession()
 const session = getMasterAdminViewSession()

 return {
 isReadOnly: isAdminAssisted, // Legacy support
 isAdminAssisted,
 canPurchase: session?.permissions?.canPurchase ?? false,
 session
 }
}

/**
 * Get a user-friendly description of current admin assistance mode
 */
export const getAssistanceModeDescription = (): string | null => {
 const session = getMasterAdminViewSession()
 if (!session) return null

 const username = session.targetUsername || session.targetEmail
 return `Admin Assistance Mode - Helping ${username}`
}

